import { useBehavior } from '@libs/core';
import { TCommonData, TViewCommonOption, TWidgetCommonOption, TWidget, TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, CSSProperties } from 'react';
import { useThemeHook } from '../hooks/themeHook';
import { mergeWithTheme } from '../utils/styles';
import { ALIGNMENT_ENUM, TCoreOptions, TCoreDividerOptions } from '../types/options';
import styles from './divider.module.css';

const ALIGNMENT_CLASS: Record<string, string> = {
    [ALIGNMENT_ENUM.TOP_LEFT]: styles.alignTopLeft,
    [ALIGNMENT_ENUM.TOP_CENTER]: styles.alignTopCenter,
    [ALIGNMENT_ENUM.TOP_RIGHT]: styles.alignTopRight,
    [ALIGNMENT_ENUM.CENTER_LEFT]: styles.alignCenterLeft,
    [ALIGNMENT_ENUM.CENTER]: styles.alignCenter,
    [ALIGNMENT_ENUM.CENTER_RIGHT]: styles.alignCenterRight,
    [ALIGNMENT_ENUM.BOTTOM_LEFT]: styles.alignBottomLeft,
    [ALIGNMENT_ENUM.BOTTOM_CENTER]: styles.alignBottomCenter,
    [ALIGNMENT_ENUM.BOTTOM_RIGHT]: styles.alignBottomRight,
};

type TCoreDividerStyleProps = Pick<TCoreDividerOptions, 'outlineVariant' | 'margin' | 'alignment' | 'thickness'>;

type TCoreDividerStandaloneProps = {
    isWidget: false;
    uid?: string;
    styleProps?: TCoreDividerStyleProps;
} & Pick<TWidgetCommonOption, 'isHide'> &
    Pick<TViewCommonOption, 'styleName' | 'parentStyleNames'>;

type TCoreDividerProps = ({ isWidget: true } & TWidgetProps<TCommonData, TCoreOptions>) | TCoreDividerStandaloneProps;

const getValue = (v?: number | string, postfix?: string) => (v != null ? `${v}${postfix ?? ''}` : undefined);

const buildDividerCssVars = (palette: TCoreDividerOptions): Record<string, string | undefined> => {
    const margin = palette.margin ?? {};
    return {
        '--cw-divider-color': palette.outlineVariant,
        '--cw-divider-thickness': getValue(palette.thickness, 'px'),
        '--cw-divider-margin-top': getValue(margin.top, 'px'),
        '--cw-divider-margin-right': getValue(margin.right, 'px'),
        '--cw-divider-margin-bottom': getValue(margin.bottom, 'px'),
        '--cw-divider-margin-left': getValue(margin.left, 'px'),
    };
};

const CoreDivider = (props: TCoreDividerProps) => {
    const finalWidget: TWidget<TCommonData, TCoreDividerOptions> = props.isWidget
        ? props.widget
        : ({
              uid: props.uid ?? '',
              data: {},
              options: {
                  isHide: props.isHide,
                  style: props.styleName,
                  parentStyleNames: props.parentStyleNames,
              } as TCoreDividerOptions,
          } as TWidget<TCommonData, TCoreDividerOptions>);

    const { palette: themePalette } = useThemeHook(finalWidget);
    const { isHidden } = useBehavior(finalWidget);

    const palette: TCoreDividerOptions = props.isWidget ? themePalette : mergeWithTheme(themePalette, props.styleProps);

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <hr
                data-test={finalWidget.uid}
                className={clsx(styles.divider, palette.alignment && ALIGNMENT_CLASS[palette.alignment])}
                style={buildDividerCssVars(palette) as CSSProperties}
            />
        </Activity>
    );
};

export { CoreDivider };
